import type { FC } from 'react'
import React, { memo } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { ResponsiveBar } from '@nivo/bar'
import {
  formatRating,
  getChartTooltipDate,
  isEqual,
  swapObjectKeysValues,
} from '@homeviews/utils'

import {
  useGetReviewsStatsQuery,
  useGetAdditionalQuestionsBenchmarkQuery,
  isEngageUser,
} from '@homeviews/store'
import { transformBtrAdditionalQuestionsChartData } from '../../helpers/transform'
import { chartTheme } from '../../helpers/layout'
import { CHART_BRAND_COLORS } from '../../helpers/settings'
import ChartLegend from '../ChartLegend'
import ChartTooltip from '../ChartTooltip'
import ScreenshotButton from '../ScreenshotButton'
import { Spinner, Typography } from '@homeviews/shared-ui'
import { getPreviousPeriod } from '@homeviews/utils'
import CustomBar from '../CustomBar'
import NoData from '../NoData'
import BenchmarkHorizontalLayer from '../BenchmarkHorizontalLayer'
import { useSelector } from 'react-redux'

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    title: {
      marginBottom: 16,
    },
  }),
)

interface IProps {
  activeFilters: any
  graphDisabled
  gridRef?: HTMLElement
  isBtr?: boolean
  filtersDetails: any
}

const BTR_QUESTIONS_MAP = {
  tenant_rating_additional: 'Additional Services',
  tenant_rating_repairs: 'Repairs and Maintenance',
  tenant_rating_service: 'Customer Service',
}

const BTS_QUESTIONS_MAP = {
  owner_rating_service: 'Customer Service',
  owner_rating_aftersales: 'After Sales Care',
  owner_rating_quality: 'Delivery Quality',
  owner_rating_timetable: 'Delivery Timetable',
}

const BtrAdditionalQuestionsChart: FC<IProps> = memo(
  ({ activeFilters, graphDisabled, gridRef, isBtr, filtersDetails }) => {
    const classes = useStyles()

    const { dateFrom: prevDateFrom, dateTo: prevDateTo } = getPreviousPeriod(
      activeFilters?.date_from,
      activeFilters?.date_to,
    )

    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const {
      data: ownerAdditionalQuestions,
      isFetching: isFetchingOwnerAdditionalQuestions,
    } = useGetReviewsStatsQuery(
      {
        'reviewer_type[]': isBtr ? 'tenant' : ['owner', 'shared_owner'],
        'development_type[]': isBtr ? 'btr' : ['bts', 'hd', 'll', 'ha'],
        aggregate: 'avg',
        aggregate_fieldset: isBtr ? 'tenant' : 'owner',
        ...activeFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled || isBtr === undefined,
      },
    )

    const { data: benchmarkData, isFetching: isFetchingBenchmarkData } =
      useGetAdditionalQuestionsBenchmarkQuery(
        {
          'reviewer_type[]': isBtr ? 'tenant' : ['owner', 'shared_owner'],
          'development_type[]': isBtr ? 'btr' : ['bts', 'hd', 'll', 'ha'],
          aggregate: 'avg',
          aggregate_fieldset: isBtr ? 'tenant' : 'owner',
          ...activeFilters,
          engage: isEngagePackage,
        },
        {
          skip: graphDisabled || isBtr === undefined,
        },
      )

    const {
      data: prevOwnerAdditionalQuestions,
      isFetching: isFetchingPrevOwnerAdditionalQuestions,
    } = useGetReviewsStatsQuery(
      {
        'reviewer_type[]': isBtr ? 'tenant' : ['owner', 'shared_owner'],
        'development_type[]': isBtr ? 'btr' : ['bts', 'hd', 'll', 'ha'],
        aggregate: 'avg',
        aggregate_fieldset: isBtr ? 'tenant' : 'owner',
        ...activeFilters,
        date_from: prevDateFrom,
        date_to: prevDateTo,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled || isBtr === undefined,
      },
    )

    const chartData = transformBtrAdditionalQuestionsChartData(
      ownerAdditionalQuestions?.data,
      prevOwnerAdditionalQuestions?.data,
      benchmarkData?.data,
      isBtr ? BTR_QUESTIONS_MAP : BTS_QUESTIONS_MAP,
      isBtr,
    )

    if (!chartData || !chartData?.length) {
      if (gridRef) {
        gridRef.style.display = 'none'
      }
      return <NoData />
    } else {
      if (gridRef) {
        gridRef.style.display = 'block'
      }
    }

    if (
      isFetchingOwnerAdditionalQuestions ||
      isFetchingPrevOwnerAdditionalQuestions ||
      isFetchingBenchmarkData
    ) {
      return <Spinner centered />
    }

    const periodFilter = true

    let formattedDateRange =
      !prevDateFrom || !prevDateTo
        ? ''
        : `${getChartTooltipDate(prevDateFrom)} - ${getChartTooltipDate(
            prevDateTo,
          )}`

    if (formattedDateRange) {
      formattedDateRange = `Previous Period (${formattedDateRange})`
    }

    let hasPrevData = false
    let chartKeys = ['Current Rating']
    Object.keys(isBtr ? BTR_QUESTIONS_MAP : BTS_QUESTIONS_MAP).map(
      (itemKey: any) => {
        if (
          prevOwnerAdditionalQuestions?.data?.[itemKey] &&
          prevOwnerAdditionalQuestions?.data?.[itemKey] !== '0.00'
        ) {
          hasPrevData = true
        }
      },
    )

    if (hasPrevData) {
      chartKeys = ['Current Rating', 'Previous Year Average']
    }

    return (
      <>
        <div className="h-exportableDataTitle">
          <Typography variant="h3" className={classes.title}>
            {isBtr === true
              ? 'Build to Rent'
              : 'Build to Sell - New Build Owner'}{' '}
            additional questions
          </Typography>
          <ScreenshotButton />
        </div>
        {filtersDetails && filtersDetails}
        <div className="l-chartContainer">
          <div className="l-chartWrapper">
            <ResponsiveBar
              data={chartData}
              theme={chartTheme}
              keys={chartKeys}
              colors={[
                CHART_BRAND_COLORS.green1,
                CHART_BRAND_COLORS.darkBlue3,
                CHART_BRAND_COLORS.gold2,
              ]}
              isInteractive={true}
              enableGridX={false}
              enableLabel={true}
              groupMode="grouped"
              padding={0.65}
              innerPadding={10}
              maxValue={5}
              borderRadius={2}
              axisBottom={{
                tickSize: 0,
                tickPadding: 10,
                tickRotation: 0,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 10,
                tickRotation: 0,
                tickValues: [0, 1, 2, 3, 4, 5],
              }}
              margin={{ top: 50, right: 20, bottom: 30, left: 30 }}
              label={(labelValue) => {
                return (
                  <tspan y={-12}>
                    {formatRating(labelValue.formattedValue)}
                  </tspan>
                ) as unknown as string
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor="#4E516B"
              animate={false}
              barComponent={(barProps) => (
                <CustomBar {...barProps} barLayout="questionsChart" />
              )}
              layers={[
                'grid',
                'axes',
                (layerProps) => (
                  <BenchmarkHorizontalLayer
                    dataProps={{
                      data: benchmarkData?.data?.[isBtr ? 'tenant' : 'owner'],
                      labels: swapObjectKeysValues(
                        isBtr ? BTR_QUESTIONS_MAP : BTS_QUESTIONS_MAP,
                      ),
                    }}
                    layerProps={layerProps}
                  />
                ),
                'bars',
                'markers',
                'legends',
                'annotations',
              ]}
              tooltip={(tooltip: any) => {
                let tooltipText = ''
                if (tooltip?.id === 'Previous Year Average') {
                  tooltipText = periodFilter
                    ? formattedDateRange
                    : `Previous Year Rating`
                } else {
                  tooltipText = tooltip.id
                }
                return (
                  <ChartTooltip>
                    <ChartLegend
                      direction="vertical"
                      data={[
                        {
                          text: `${tooltipText}: ${
                            tooltip?.formattedValue
                              ? formatRating(tooltip.formattedValue)
                              : ''
                          }`,
                          shapeColor: tooltip?.color,
                        },
                      ]}
                    />
                  </ChartTooltip>
                )
              }}
            />
          </div>
          <ChartLegend
            data={[
              {
                text: 'Current Rating',
                shapeColor: CHART_BRAND_COLORS.green1,
              },
              hasPrevData && {
                text: periodFilter
                  ? formattedDateRange
                  : `Previous Year Rating`,
                shapeColor: CHART_BRAND_COLORS.darkBlue3,
              },
              {
                text: `Benchmark (${
                  isBtr ? 'Build to Rent' : 'Build to Sell'
                })`,
                shapeColor: CHART_BRAND_COLORS.gold2,
                tooltipText: isBtr
                  ? 'Discover the HomeViews UK benchmark specific to Build to Rent. This is based on all time data up to the last full half year period.'
                  : 'Discover the HomeViews UK benchmark specific, based upon reviews in Build to Sell properties. This is based on all time data up to the last full half year period.',
              },
            ]}
          />
        </div>
      </>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default BtrAdditionalQuestionsChart
